import { useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Tooltip from '@mui/material/Tooltip';
import { useHandleSubmitWrongPolicyNoCDV } from 'forms/hooks';
import { addIndex, compose, filter, includes, map } from 'ramda';
import { useDispatch, useSelector } from 'react-redux';

import Autocomplete from '@gef-ui/form-material/Autocomplete';
import SubmitButton from '@gef-ui/form-material/SubmitButton';
import TextField from '@gef-ui/form-material/TextField';
import { getLabelMessage } from '@gef-ui/localization/messages';

import AutocompleteCustomOptionKey from 'components/atoms/GefMui/AutocompleteCustomOptionKey';
import Agency from 'components/atoms/GefMui/OrganizationStructure/Agency';
import Channel from 'components/atoms/GefMui/OrganizationStructure/Channel';
import Manager from 'components/atoms/GefMui/OrganizationStructure/Manager';
import Ppz from 'components/atoms/GefMui/OrganizationStructure/Ppz';
import Region from 'components/atoms/GefMui/OrganizationStructure/Region';
import Seller from 'components/atoms/GefMui/OrganizationStructure/Seller';
import { codelistProvider } from 'components/index';
import { codelists } from 'constants/codelists';
import { MaxLengths } from 'constants/forms';
import { AUTO_FILL_RULES, monthsArray, USER_ROLES } from 'constants/general';
import { CAMPAIGN_MANAGEMENT_FORM_NAME, FIELDS } from 'features/views/campaignManagement/campaignDetail/form';
import contacts from 'features/views/campaignManagement/contacts';
import user from 'features/views/user';
import { joinString } from 'utils/string';

export const Form = ({ products, contactStates, assignLevels, printTypes }) => {
	const dispatch = useDispatch();

	const currentRole = useSelector(user.selectors.getCurrentRole);

	const productsOptions = useMemo(
		() =>
			map((item) => ({
				...item,
				fullDescription: joinString(' - ', [item?.shortDescription, item?.jokName]),
			}))(products),
		[products],
	);

	const contactStatesOptions = useMemo(
		() =>
			filter((item) => {
				if (currentRole === USER_ROLES.PPZ || currentRole === USER_ROLES.SELLER) {
					return item.sellerPpz;
				} else return true;
			})(contactStates),
		[contactStates, currentRole],
	);

	const mapWithIndex = addIndex(map);
	const monthOptions = useMemo(
		() =>
			mapWithIndex((month, i) => {
				const index = i + 1;
				return {
					code: index,
					name: [`${index}`.padStart(2, '0'), month.charAt(0).toUpperCase() + month.slice(1)].join(' - '),
				};
			})(monthsArray),
		[mapWithIndex],
	);

	useEffect(() => {
		dispatch(user.actions.fetchAdditionalUserInfo());
	}, [dispatch]);

	const handleReset = useCallback(() => {
		dispatch(contacts.actions.resetCriteria());
	}, [dispatch]);

	const handleSubmit = useCallback(() => {
		dispatch(contacts.actions.submitFilter());
	}, [dispatch]);

	const handleSubmitWrongPolicyNoCDV = useHandleSubmitWrongPolicyNoCDV({ continueFn: handleSubmit });

	return (
		<Grid container>
			<Grid size={12}>
				<Grid container spacing={2}>
					<Grid size={4}>
						<Channel
							fieldName={FIELDS.channel}
							formName={CAMPAIGN_MANAGEMENT_FORM_NAME}
							disableOnOneOptions={!includes(currentRole, AUTO_FILL_RULES.CENTRAL)}
							autoSelect
							isGuarantor={currentRole === USER_ROLES.GUARANTOR}
						/>
					</Grid>
					<Grid size={4}>
						<Region
							fieldName={FIELDS.region}
							formName={CAMPAIGN_MANAGEMENT_FORM_NAME}
							disableOnOneOptions={!includes(currentRole, AUTO_FILL_RULES.CENTRAL)}
							autoSelect
							isGuarantor={currentRole === USER_ROLES.GUARANTOR}
						/>
					</Grid>
					<Grid size={4}>
						<Agency
							fieldName={FIELDS.agency}
							formName={CAMPAIGN_MANAGEMENT_FORM_NAME}
							disableOnOneOptions={!includes(currentRole, AUTO_FILL_RULES.REGION_MANAGER)}
							autoSelect
						/>
					</Grid>
					<Grid size={4}>
						<Manager
							fieldName={FIELDS.manager}
							formName={CAMPAIGN_MANAGEMENT_FORM_NAME}
							disableOnOneOptions={!includes(currentRole, AUTO_FILL_RULES.AGENCY_MANAGER)}
							autoSelect
						/>
					</Grid>
					<Grid size={4}>
						<Seller
							fieldName={FIELDS.seller}
							formName={CAMPAIGN_MANAGEMENT_FORM_NAME}
							disableOnOneOptions={!includes(currentRole, AUTO_FILL_RULES.SELLER)}
							autoSelect
						/>
					</Grid>
					<Grid size={4}>
						<Ppz
							fieldName={FIELDS.ppz}
							formName={CAMPAIGN_MANAGEMENT_FORM_NAME}
							disableOnOneOptions
							autoSelect
						/>
					</Grid>
					<Tooltip title={getLabelMessage('tooltips:policyNo')}>
						<Grid size={2}>
							<TextField
								size="small"
								fieldName={FIELDS.policyNumber}
								label={getLabelMessage('campaignManagement:policyNo')}
								slotProps={{
									htmlInput: {
										maxLength: MaxLengths.POLICY_NO,
									},
								}}
							/>
						</Grid>
					</Tooltip>
					<Grid size={3}>
						<Autocomplete
							size="small"
							fieldName={FIELDS.policyEndDate}
							label={getLabelMessage('campaignManagement:policyFinishDate')}
							options={monthOptions}
							labelPropName="name"
							valuePropName="code"
						/>
					</Grid>
					<Grid size={3}>
						<AutocompleteCustomOptionKey
							size="small"
							fieldName={FIELDS.product}
							label={getLabelMessage('campaignManagement:product')}
							options={productsOptions}
							labelPropName="fullDescription"
							keyPropName="id"
						/>
					</Grid>
					<Grid size={4}>
						<Autocomplete
							size="small"
							fieldName={FIELDS.contactState}
							label={getLabelMessage('campaignManagement:contactState')}
							options={contactStatesOptions}
							labelPropName="shortDescription"
						/>
					</Grid>
					<Grid size={3}>
						<Autocomplete
							size="small"
							fieldName={FIELDS.assignLevel}
							label={getLabelMessage('campaignManagement:assignLevel')}
							options={assignLevels}
							labelPropName="shortDescription"
						/>
					</Grid>
					<Grid size={3}>
						<TextField
							size="small"
							fieldName={FIELDS.firstName}
							label={getLabelMessage('campaignManagement:firstName')}
							slotProps={{
								htmlInput: {
									maxLength: MaxLengths.NAME,
								},
							}}
						/>
					</Grid>
					<Grid size={3}>
						<TextField
							size="small"
							fieldName={FIELDS.surname}
							label={getLabelMessage('campaignManagement:surname')}
							slotProps={{
								htmlInput: {
									maxLength: MaxLengths.SURNAME,
								},
							}}
						/>
					</Grid>
					<Grid size={3}>
						<TextField
							size="small"
							fieldName={FIELDS.businessName}
							label={getLabelMessage('campaignManagement:businessName')}
							slotProps={{
								htmlInput: {
									maxLength: MaxLengths.BUSINESS_NAME,
								},
							}}
						/>
					</Grid>
					<Tooltip title={getLabelMessage('tooltips:birthNumber')}>
						<Grid size={2}>
							<TextField
								size="small"
								fieldName={FIELDS.personIn}
								label={getLabelMessage('campaignManagement:personIn')}
								slotProps={{
									htmlInput: {
										maxLength: MaxLengths.BIRTH_NUMBER,
									},
								}}
							/>
						</Grid>
					</Tooltip>
					<Tooltip title={getLabelMessage('tooltips:taxNumber')}>
						<Grid size={2}>
							<TextField
								size="small"
								fieldName={FIELDS.businessIn}
								label={getLabelMessage('campaignManagement:businessIn')}
								slotProps={{
									htmlInput: {
										maxLength: MaxLengths.TAX_NUMBER,
									},
								}}
							/>
						</Grid>
					</Tooltip>
					<Grid size={3}>
						<TextField
							size="small"
							fieldName={FIELDS.municipality}
							label={getLabelMessage('campaignManagement:municipality')}
							slotProps={{
								htmlInput: {
									maxLength: MaxLengths.MUNICIPALITY,
								},
							}}
						/>
					</Grid>
					<Tooltip title={getLabelMessage('tooltips:postalCode')}>
						<Grid size={2}>
							<TextField
								size="small"
								fieldName={FIELDS.zipCode}
								label={getLabelMessage('campaignManagement:zipCode')}
								slotProps={{
									htmlInput: {
										maxLength: MaxLengths.ZIP_CODE,
									},
								}}
							/>
						</Grid>
					</Tooltip>
					<Grid size={3}>
						<TextField
							size="small"
							fieldName={FIELDS.policyAgent}
							label={getLabelMessage('campaignManagement:policyAgent')}
							slotProps={{
								htmlInput: {
									maxLength: MaxLengths.POLICY_AGENT,
								},
							}}
						/>
					</Grid>
					<Grid size={3}>
						<Autocomplete
							size="small"
							fieldName={FIELDS.printType}
							label={getLabelMessage('campaignManagement:printType')}
							options={printTypes}
							labelPropName="shortDescription"
						/>
					</Grid>
					<Grid size={3} />
					<Grid size={3}>
						<Button variant="outlined" onClick={handleReset} fullWidth>
							{getLabelMessage('common:resetButton')}
						</Button>
					</Grid>
					<Grid size={3}>
						<SubmitButton
							variant="contained"
							onClick={handleSubmit}
							onClickError={handleSubmitWrongPolicyNoCDV}
							fullWidth
							text={getLabelMessage('common:searchButton')}
						/>
					</Grid>
				</Grid>
			</Grid>
		</Grid>
	);
};

Form.propTypes = {
	assignLevels: PropTypes.array,
	contactStates: PropTypes.array,
	printTypes: PropTypes.array,
	products: PropTypes.array,
};

export default compose(
	codelistProvider(codelists.productTypes, 'products'),
	codelistProvider(codelists.solutionStateVirtual, 'contactStates'),
	codelistProvider(codelists.assignLevels, 'assignLevels'),
	codelistProvider(codelists.printTypes, 'printTypes'),
)(Form);
